import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Text from 'components_new/atoms/Text'

import ChipTabs from 'components_new/molecules/ChipTabs'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'

import dayjs from 'utils/dayjs'
import { FileDelivery, JobSummary } from 'redux/selectors/internal/DataRuns'
import {
  deliveryKey,
  deliveryLabel,
  DELIVERY_BORDER_COLOR,
  DELIVERY_FILL_COLOR,
  DELIVERY_STATUS_FILL_COLOR,
  DELIVERY_STATUS_LABEL,
  formatDayClock,
  formatDurationMin,
  jobKey,
  RUNNING_PULSE_SX,
  STATUS_BORDER_COLOR,
  STATUS_FILL_COLOR
} from '../utils'
import {
  buildTicks,
  computeWindow,
  getDeliveryMarkers,
  getJobRuns,
  percentInWindow,
  pickTickMinutes,
  placeDeliveryMarkers,
  runIntersectsWindow,
  TimelineMarker,
  TimelineRun,
  TimelineWindow,
  TimelineWindowMode
} from './utils'

export interface DataRunsTimelineProps {
  /**
   * Customer file sources, drawn as instant markers above the job lanes -
   * the file arrives before the job that reads it, which is the whole point
   * of putting them in one picture.
   */
  fileDeliveries: FileDelivery[]
  jobs: JobSummary[]
  onSelectFileDelivery: (delivery: FileDelivery) => void
  onSelectJobRun: (job: JobSummary) => void
}

const LABEL_COLUMN_WIDTH = 250
const MIN_BAR_WIDTH_PCT = 0.7
const FLIP_LABEL_THRESHOLD_PCT = 72

const WINDOW_OPTIONS: { label: string; value: TimelineWindowMode }[] = [
  { label: 'Kundens körning', value: 'auto' },
  { label: 'Kväll–morgon', value: 'evening' },
  { label: 'Hela dygnet', value: 'day' }
]

interface TimelineRowData {
  job: JobSummary
  runs: TimelineRun[]
}

/**
 * A single bar on a lane - absolutely positioned within the (flexible)
 * track, with its label placed outside the bar so short bars stay legible.
 */
const TimelineBar = (props: {
  run: TimelineRun
  timelineWindow: TimelineWindow
}) => {
  const { run, timelineWindow } = props

  const startPct = percentInWindow(run.start, timelineWindow)
  const endPct = percentInWindow(run.end, timelineWindow)
  const widthPct = Math.max(MIN_BAR_WIDTH_PCT, endPct - startPct)
  const flip = endPct > FLIP_LABEL_THRESHOLD_PCT

  const label = run.isRunning
    ? `${run.start.format('HH:mm')} → pågår`
    : `${run.start.format('HH:mm')}–${run.end.format(
      'HH:mm'
    )} · ${formatDurationMin(
      run.durationMin ?? run.end.diff(run.start, 'minute')
    )}`

  return (
    <>
      <Box
        sx={{
          bgcolor: STATUS_FILL_COLOR[run.status],
          border: '1px solid',
          borderColor: STATUS_BORDER_COLOR[run.status],
          borderRadius: '5px',
          height: '14px',
          left: `${startPct}%`,
          position: 'absolute',
          top: '5px',
          width: `${widthPct}%`,
          ...(run.isRunning ? RUNNING_PULSE_SX : null)
        }}
      />
      <Text
        sx={{
          color: 'text.disabled',
          fontVariantNumeric: 'tabular-nums',
          left: flip ? `${startPct}%` : `${startPct + widthPct}%`,
          position: 'absolute',
          top: 0,
          transform: flip ? 'translateX(calc(-100% - 4px))' : 'translateX(4px)',
          whiteSpace: 'nowrap'
        }}
        variant="caption"
      >
        {label}
      </Text>
    </>
  )
}

/**
 * A single file delivery: an instant, so a diamond rather than a bar.
 *
 * `pinned` renders it flush against whichever edge of the window it fell
 * outside, dimmed and with the full date in the label. Dropping those
 * markers instead (which is what `runIntersectsWindow` does for job runs)
 * would leave the lane blank and read as "never delivered".
 */
const TimelineDeliveryMarker = (props: {
  marker: TimelineMarker
  pinned: 'before' | 'after' | null
  timelineWindow: TimelineWindow
}) => {
  const { marker, pinned, timelineWindow } = props

  const leftPct = pinned
    ? pinned === 'before'
      ? 0
      : 100
    : percentInWindow(marker.at, timelineWindow)
  const flip = leftPct > FLIP_LABEL_THRESHOLD_PCT
  const label = pinned
    ? `${pinned === 'before' ? '◀' : '▶'} ${formatDayClock(
      marker.at.toISOString()
    )}`
    : marker.at.format('HH:mm')

  return (
    <>
      <Box
        sx={{
          bgcolor: DELIVERY_FILL_COLOR[marker.status],
          border: '1px solid',
          borderColor: DELIVERY_BORDER_COLOR[marker.status],
          height: '10px',
          left: `${leftPct}%`,
          opacity: pinned ? 0.45 : 1,
          position: 'absolute',
          top: '7px',
          transform: 'translateX(-50%) rotate(45deg)',
          width: '10px'
        }}
      />
      <Text
        sx={{
          color: 'text.disabled',
          fontVariantNumeric: 'tabular-nums',
          left: `${leftPct}%`,
          opacity: pinned ? 0.75 : 1,
          position: 'absolute',
          top: 0,
          transform: flip ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
          whiteSpace: 'nowrap'
        }}
        variant="caption"
      >
        {label}
      </Text>
    </>
  )
}

/**
 * "Tidslinje" - one row per job, sorted by start time, with a window
 * selector (customer's own run / evening-morning / full day). The only
 * gantt-like visualization in the codebase - built new since nothing
 * existing covers a horizontal timeline.
 *
 * The axis's tick interval is derived from the measured track width via a
 * `ResizeObserver` on a zero-height probe element placed inside the (fixed-
 * width-independent) track column. The probe's own size only changes when
 * the card's layout width actually changes - not when `trackWidth` state
 * updates trigger a re-render - so this cannot loop.
 */
const DataRunsTimeline = (props: DataRunsTimelineProps) => {
  const { fileDeliveries, jobs, onSelectFileDelivery, onSelectJobRun } = props

  const theme = useTheme()

  const [windowMode, setWindowMode] = useState<TimelineWindowMode>('auto')
  const [trackWidth, setTrackWidth] = useState(0)
  const probeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const probe = probeRef.current

    if (!probe) {
      return undefined
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]

      if (!entry) {
        return
      }

      const width = Math.round(entry.contentRect.width)

      // Guarded: only set state when the width actually changed, so a
      // ResizeObserver firing with an unchanged size can never trigger a
      // resize -> render -> resize loop.
      setTrackWidth((prev) => (prev === width ? prev : width))
    })

    observer.observe(probe)

    return () => observer.disconnect()
  }, [])

  const now = useMemo(() => dayjs(), [jobs])

  const jobRuns = useMemo<TimelineRowData[]>(
    () => jobs.map((job) => ({ job, runs: getJobRuns(job, now) })),
    [jobs, now]
  )

  const allRuns = useMemo(() => jobRuns.flatMap((row) => row.runs), [jobRuns])

  /**
   * Measured over the Glue runs only, never over deliveries. A monthly file
   * source would otherwise stretch a window built for hours out to weeks
   * and make the whole timeline unreadable - deliveries are positioned
   * within the window the jobs define, and pinned to its edges when they
   * fall outside. Do not "fix" this by passing deliveries in.
   */
  const timelineWindow = useMemo(
    () => computeWindow(windowMode, allRuns, now),
    [windowMode, allRuns, now]
  )

  const spanMin = timelineWindow.end.diff(timelineWindow.start, 'minute')
  const tickMin = useMemo(
    () => pickTickMinutes(trackWidth, spanMin),
    [trackWidth, spanMin]
  )
  const ticks = useMemo(
    () => (tickMin === null ? [] : buildTicks(timelineWindow, tickMin)),
    [timelineWindow, tickMin]
  )
  const trackRaster =
    tickMin === null
      ? 'none'
      : `repeating-linear-gradient(to right, ${theme.palette.divider} 0, ${
        theme.palette.divider
      } 1px, transparent 1px, transparent ${
        spanMin > 0 ? (tickMin / spanMin) * 100 : 100
      }%)`

  const rows = useMemo(() => {
    return jobRuns
      .map(({ job, runs }) => ({
        job,
        runs: runs.filter((run) => runIntersectsWindow(run, timelineWindow))
      }))
      .filter((row) => row.runs.length > 0)
      .sort((a, b) => a.runs[0].start.valueOf() - b.runs[0].start.valueOf())
  }, [jobRuns, timelineWindow])

  /**
   * Every file source keeps its lane, unlike the job rows above - a source
   * with nothing in the window still has something true to say (the pinned
   * "◀ 4 apr 04:30", or that it has never delivered at all).
   */
  const deliveryRows = useMemo(
    () =>
      fileDeliveries.map((delivery) => ({
        delivery,
        placed: placeDeliveryMarkers(
          getDeliveryMarkers(delivery),
          timelineWindow
        )
      })),
    [fileDeliveries, timelineWindow]
  )

  const isEmpty = jobs.length === 0 && fileDeliveries.length === 0

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px'
      }}
    >
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: 'space-between'
        }}
      >
        <SectionHeader
          body="en filleverans eller ett jobb per rad · klicka för detaljer"
          title={`Tidslinje ${timelineWindow.start.format(
            'HH:mm'
          )} – ${timelineWindow.end.format('HH:mm')}`}
        />
        <ChipTabs
          disableAll
          onChange={(value) => setWindowMode(value as TimelineWindowMode)}
          options={WINDOW_OPTIONS}
          size="small"
          value={windowMode}
        />
      </Box>

      {isEmpty ? (
        <EmptyPlaceholder title="Inga körningar att visa." />
      ) : (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', mb: 0.5 }}>
            <Box sx={{ flexShrink: 0, width: LABEL_COLUMN_WIDTH }} />
            <Box
              sx={{
                flex: 1,
                height: '16px',
                minWidth: 0,
                position: 'relative'
              }}
            >
              <Box ref={probeRef} sx={{ height: 0, width: '100%' }} />
              {ticks.map((tick) => (
                <Text
                  key={tick.toISOString()}
                  sx={{
                    color: 'text.disabled',
                    fontVariantNumeric: 'tabular-nums',
                    left: `${percentInWindow(tick, timelineWindow)}%`,
                    position: 'absolute',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap'
                  }}
                  variant="caption"
                >
                  {tick.format('HH:mm')}
                </Text>
              ))}
            </Box>
          </Box>

          {deliveryRows.length > 0 ? (
            <Box sx={{ mb: 1.5 }}>
              <Text
                sx={{
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  mb: 0.5,
                  textTransform: 'uppercase'
                }}
              >
                Filleveranser
              </Text>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
              >
                {deliveryRows.map(({ delivery, placed }) => (
                  <Box
                    key={deliveryKey(delivery)}
                    onClick={() => onSelectFileDelivery(delivery)}
                    sx={{
                      alignItems: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      height: '32px',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexShrink: 0,
                        gap: 1,
                        minWidth: 0,
                        pr: 1,
                        width: LABEL_COLUMN_WIDTH
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: DELIVERY_STATUS_FILL_COLOR[delivery.status],
                          flexShrink: 0,
                          height: '7px',
                          transform: 'rotate(45deg)',
                          width: '7px'
                        }}
                      />
                      <Text
                        sx={{
                          fontWeight: 600,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        variant="body2"
                      >
                        {deliveryLabel(delivery)}
                      </Text>
                    </Box>

                    <Box
                      sx={{
                        backgroundImage: trackRaster,
                        borderRadius: '6px',
                        flex: 1,
                        height: '24px',
                        minWidth: 0,
                        position: 'relative'
                      }}
                    >
                      {placed.inside.map((marker) => (
                        <TimelineDeliveryMarker
                          key={marker.key}
                          marker={marker}
                          pinned={null}
                          timelineWindow={timelineWindow}
                        />
                      ))}
                      {placed.pinnedBefore ? (
                        <TimelineDeliveryMarker
                          key={placed.pinnedBefore.key}
                          marker={placed.pinnedBefore}
                          pinned="before"
                          timelineWindow={timelineWindow}
                        />
                      ) : null}
                      {placed.pinnedAfter ? (
                        <TimelineDeliveryMarker
                          key={placed.pinnedAfter.key}
                          marker={placed.pinnedAfter}
                          pinned="after"
                          timelineWindow={timelineWindow}
                        />
                      ) : null}
                      {placed.inside.length === 0 &&
                      !placed.pinnedBefore &&
                      !placed.pinnedAfter ? (
                            <Text
                              sx={{
                                color: 'text.disabled',
                                left: 0,
                                position: 'absolute',
                                top: '2px'
                              }}
                              variant="caption"
                            >
                              {DELIVERY_STATUS_LABEL[delivery.status]} · ingen
                          leverans
                            </Text>
                          ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          {rows.length === 0 ? (
            <EmptyPlaceholder title="Inga körningar i vald tidsperiod." />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {rows.map(({ job, runs }) => (
                <Box
                  key={jobKey(job)}
                  onClick={() => onSelectJobRun(job)}
                  sx={{
                    alignItems: 'center',
                    bgcolor:
                      job.status === 'fail' || job.status === 'timeout'
                        ? 'error.transparent'
                        : undefined,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    height: '32px',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexShrink: 0,
                      gap: 1,
                      minWidth: 0,
                      pr: 1,
                      width: LABEL_COLUMN_WIDTH
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: STATUS_FILL_COLOR[job.status],
                        borderRadius: '50%',
                        flexShrink: 0,
                        height: '7px',
                        width: '7px'
                      }}
                    />
                    <Text
                      sx={{
                        color:
                          job.status === 'fail' ? 'error.text' : 'text.primary',
                        fontWeight: 600,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      variant="body2"
                    >
                      {job.job_name}
                    </Text>
                  </Box>

                  <Box
                    sx={{
                      backgroundImage: trackRaster,
                      borderRadius: '6px',
                      flex: 1,
                      height: '24px',
                      minWidth: 0,
                      position: 'relative'
                    }}
                  >
                    {runs.map((run) => (
                      <TimelineBar
                        key={run.key}
                        run={run}
                        timelineWindow={timelineWindow}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

DataRunsTimeline.displayName = 'DataRunsTimeline'
export default DataRunsTimeline
